import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "../lib/api";

const FRAME =
  "relative w-full overflow-hidden rounded-[1.3rem] border bg-[hsl(var(--muted))] shadow-[var(--shadow-soft)]";

// Hero media: a single image OR a swipeable image carousel (dots + optional
// auto-rotate). Video lives in its own section now, never in the hero.
export default function HeroMedia({ hero }) {
  const type = hero?.type || "single";
  const v = Number(hero?.version || 0);
  const count = Number(hero?.carousel_count || 0);
  const autoRotate = hero?.auto_rotate !== false;
  const interval = Math.max(2, Number(hero?.interval_seconds || 5)) * 1000;

  const isCarousel = type === "carousel" && count > 0;
  const images = isCarousel
    ? Array.from({ length: count }, (_, i) => `${API}/media/hero-carousel/${i}?v=${v}`)
    : [hero?.has_image ? `${API}/media/hero-image?v=${v}` : "/brand/hero.png"];

  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);
  const n = images.length;
  const goTo = useCallback((i) => setIdx(((i % n) + n) % n), [n]);

  useEffect(() => {
    if (!isCarousel || !autoRotate || n <= 1) return undefined;
    const id = setInterval(() => setIdx((p) => (p + 1) % n), interval);
    return () => clearInterval(id);
  }, [isCarousel, autoRotate, interval, n]);

  if (!isCarousel) {
    return (
      <div className={FRAME} data-testid="hero-media-image">
        <img src={images[0]} alt="Custom photo magnets from your favorite memories" className="w-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className={`${FRAME} aspect-[4/3]`}
      data-testid="hero-media-carousel"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) goTo(idx + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
    >
      <div className="flex h-full w-full transition-transform duration-500 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {images.map((src, i) => (
          <img key={i} src={src} alt={`Hero ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} className="h-full w-full shrink-0 object-cover" style={{ width: "100%" }} />
        ))}
      </div>

      {n > 1 && (
        <>
          <button type="button" aria-label="Previous" data-testid="hero-prev" onClick={() => goTo(idx - 1)}
            className="absolute left-2 top-1/2 hidden -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-white/80 text-[hsl(var(--brand-brown))] shadow sm:grid">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button type="button" aria-label="Next" data-testid="hero-next" onClick={() => goTo(idx + 1)}
            className="absolute right-2 top-1/2 hidden -translate-y-1/2 grid h-9 w-9 place-items-center rounded-full bg-white/80 text-[hsl(var(--brand-brown))] shadow sm:grid">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5" data-testid="hero-dots">
            {images.map((_, i) => (
              <button key={i} type="button" aria-label={`Go to slide ${i + 1}`} onClick={() => goTo(i)}
                className="h-1.5 rounded-full transition-all duration-200"
                style={{ width: i === idx ? 18 : 6, background: i === idx ? "hsl(var(--brand-rose))" : "rgba(255,255,255,0.8)" }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
